// src/pages/finance-portal/inventory/ProductForm.js
//
// Product create / edit form.
//
// Fields organized in sections: Basics, Pricing & Tax, GL, Stock Policy,
// Tracking, Vendor/Sourcing, Physical, Flags. Supports variants as an
// inline editable list.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Checkbox,
    Chip, CircularProgress, Divider, FormControl, FormControlLabel,
    Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper,
    Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from '../../../utils/axiosInstance';
import { FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK } from './components/inventoryFormatters';

const PRODUCT_TYPES = ['INVENTORY', 'NON_INVENTORY', 'SERVICE', 'BUNDLE', 'DIGITAL'];
const STATUSES = ['ACTIVE', 'DRAFT', 'DISCONTINUED', 'ARCHIVED'];
const VALUATION_METHODS = ['WEIGHTED_AVERAGE', 'FIFO', 'LIFO', 'SPECIFIC_ID', 'STANDARD'];

const EMPTY = {
    sku: '', name: '', description: '', barcode: '', mpn: '',
    product_type: 'INVENTORY', status: 'ACTIVE',
    category_id: null,
    unit_price: 0, unit_cost: 0, currency: 'USD',
    tax_code: '',
    revenue_account_id: null, cogs_account_id: null, inventory_account_id: null,
    valuation_method: 'WEIGHTED_AVERAGE',
    reorder_point: 0, reorder_quantity: 0, safety_stock: 0,
    min_order_qty: 0, lead_time_days: 0,
    track_lots: false, track_serials: false, track_expiry: false,
    preferred_vendor_id: null, unit_of_measure: 'EA',
    weight: null, weight_unit: 'kg', dimensions: null,
    is_sellable: true, is_purchasable: true,
    is_dropship_only: false, allow_backorder: false,
    channels: [],
    variants: [],
};

export default function ProductForm() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const editing = Boolean(productId);

    const [form, setForm] = useState(EMPTY);
    const [categories, setCategories] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const load = useCallback(async () => {
        if (!editing) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/products/${productId}`);
            if (res.data?.success) {
                const d = res.data.data;
                setForm({
                    ...EMPTY,
                    ...d,
                    variants: d.variants || [],
                });
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load product');
        } finally {
            setLoading(false);
        }
    }, [editing, productId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        axiosInstance.get('/api/finance/inventory/categories').then((r) => setCategories(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/vendors', { params: { per_page: 200 } })
            .then((r) => setVendors(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/coa', { params: { per_page: 500 } })
            .then((r) => setAccounts(r.data?.data || r.data?.accounts || [])).catch(() => {});
    }, []);

    const addVariant = () => set('variants', [...form.variants, {
        sku: '', name: '', attributes: {}, unit_price: null, unit_cost: null, is_active: true,
    }]);

    const updateVariant = (idx, k, v) => {
        const next = form.variants.slice();
        next[idx] = { ...next[idx], [k]: v };
        set('variants', next);
    };

    const removeVariant = (idx) => {
        const next = form.variants.filter((_, i) => i !== idx);
        set('variants', next);
    };

    const handleSave = async () => {
        if (!form.name.trim()) {
            setError('Name is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            if (editing) {
                const res = await axiosInstance.put(`/api/finance/inventory/products/${productId}`, form);
                if (res.data?.success) {
                    navigate(`/finance-portal/inventory/products/${productId}`);
                }
            } else {
                const res = await axiosInstance.post('/api/finance/inventory/products', form);
                if (res.data?.success) {
                    navigate(`/finance-portal/inventory/products/${res.data.data.id}`);
                }
            }
        } catch (e) {
            const serverErrors = e.response?.data?.errors;
            const msg = e.response?.data?.message || 'Save failed';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
            }
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
                    <Box>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.55rem', fontWeight: 700, color: '#0f172a' }}>
                            {editing ? `Edit ${form.sku || 'Product'}` : 'New Product'}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.88rem', color: '#64748b' }}>
                            Catalog entry · valuation method · GL mappings · reorder policy
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    variant="contained" onClick={handleSave}
                    disabled={saving} startIcon={<SaveIcon />}
                    sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                          bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}
                >
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save'}
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <Grid container spacing={3}>
                {/* Basics */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            Basics
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="SKU" value={form.sku || ''}
                                    onChange={(e) => set('sku', e.target.value)}
                                    helperText={editing ? 'SKU is immutable once set' : 'Leave empty to auto-generate'}
                                    disabled={editing} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Barcode (UPC / EAN / QR)"
                                    value={form.barcode || ''} onChange={(e) => set('barcode', e.target.value)} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Product Name" required
                                    value={form.name || ''} onChange={(e) => set('name', e.target.value)} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth size="small" label="Description" multiline rows={3}
                                    value={form.description || ''} onChange={(e) => set('description', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select value={form.product_type} label="Type"
                                        onChange={(e) => set('product_type', e.target.value)}>
                                        {PRODUCT_TYPES.map((t) => (
                                            <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select value={form.status} label="Status"
                                        onChange={(e) => set('status', e.target.value)}>
                                        {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    size="small" options={categories} getOptionLabel={(o) => o.name || ''}
                                    value={categories.find((c) => c.id === form.category_id) || null}
                                    onChange={(e, v) => set('category_id', v?.id || null)}
                                    renderInput={(params) => <TextField {...params} label="Category" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="MPN"
                                    value={form.mpn || ''} onChange={(e) => set('mpn', e.target.value)} />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Flags card */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            Flags
                        </Typography>
                        <Stack spacing={1}>
                            <FormControlLabel control={<Switch checked={form.is_sellable} onChange={(e) => set('is_sellable', e.target.checked)} />}
                                label="Sellable" />
                            <FormControlLabel control={<Switch checked={form.is_purchasable} onChange={(e) => set('is_purchasable', e.target.checked)} />}
                                label="Purchasable" />
                            <FormControlLabel control={<Switch checked={form.is_dropship_only} onChange={(e) => set('is_dropship_only', e.target.checked)} />}
                                label="Dropship only" />
                            <FormControlLabel control={<Switch checked={form.allow_backorder} onChange={(e) => set('allow_backorder', e.target.checked)} />}
                                label="Allow backorder (negative stock)" />
                        </Stack>
                    </Paper>
                </Grid>

                {/* Pricing */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            Pricing &amp; Tax
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="List Price" type="number"
                                    value={form.unit_price ?? 0}
                                    onChange={(e) => set('unit_price', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Unit Cost" type="number"
                                    value={form.unit_cost ?? 0}
                                    onChange={(e) => set('unit_cost', e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Currency"
                                    value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Tax Code"
                                    value={form.tax_code || ''} onChange={(e) => set('tax_code', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Valuation Method</InputLabel>
                                    <Select value={form.valuation_method} label="Valuation Method"
                                        onChange={(e) => set('valuation_method', e.target.value)}>
                                        {VALUATION_METHODS.map((v) => <MenuItem key={v} value={v}>{v.replace('_', ' ')}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Unit of Measure"
                                    value={form.unit_of_measure} onChange={(e) => set('unit_of_measure', e.target.value.toUpperCase())} />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Stock policy */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            Stock Policy &amp; Reorder
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Reorder Point" type="number"
                                    value={form.reorder_point ?? 0} onChange={(e) => set('reorder_point', e.target.value)}
                                    helperText="Alert when available ≤ this" />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Reorder Quantity (EOQ)" type="number"
                                    value={form.reorder_quantity ?? 0} onChange={(e) => set('reorder_quantity', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Safety Stock" type="number"
                                    value={form.safety_stock ?? 0} onChange={(e) => set('safety_stock', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Min Order Qty" type="number"
                                    value={form.min_order_qty ?? 0} onChange={(e) => set('min_order_qty', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth size="small" label="Lead Time (days)" type="number"
                                    value={form.lead_time_days ?? 0} onChange={(e) => set('lead_time_days', e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Autocomplete
                                    size="small" options={vendors}
                                    getOptionLabel={(o) => o.display_name || o.company_name || o.vendor_number || ''}
                                    value={vendors.find((v) => v.id === form.preferred_vendor_id) || null}
                                    onChange={(e, v) => set('preferred_vendor_id', v?.id || null)}
                                    renderInput={(params) => <TextField {...params} label="Preferred Vendor" />}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Tracking + Weight */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            Tracking &amp; Physical
                        </Typography>
                        <Stack spacing={1}>
                            <FormControlLabel control={<Switch checked={form.track_lots} onChange={(e) => set('track_lots', e.target.checked)} />}
                                label="Track lots / batch numbers" />
                            <FormControlLabel control={<Switch checked={form.track_serials} onChange={(e) => set('track_serials', e.target.checked)} />}
                                label="Track serial numbers" />
                            <FormControlLabel control={<Switch checked={form.track_expiry} onChange={(e) => set('track_expiry', e.target.checked)} />}
                                label="Track expiration dates" />
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" spacing={1}>
                                <TextField size="small" label="Weight" type="number" sx={{ flex: 2 }}
                                    value={form.weight ?? ''} onChange={(e) => set('weight', e.target.value)} />
                                <TextField size="small" label="Unit" sx={{ flex: 1 }}
                                    value={form.weight_unit ?? ''} onChange={(e) => set('weight_unit', e.target.value)} />
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>

                {/* GL Mappings */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                            GL Mappings (optional)
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b', mb: 2 }}>
                            Leave blank to use category or system defaults.
                        </Typography>
                        <Stack spacing={2}>
                            {['revenue_account_id', 'cogs_account_id', 'inventory_account_id'].map((k) => (
                                <Autocomplete key={k}
                                    size="small" options={accounts}
                                    getOptionLabel={(a) => `${a.account_number || ''} — ${a.name || ''}`}
                                    value={accounts.find((a) => a.id === form[k]) || null}
                                    onChange={(e, v) => set(k, v?.id || null)}
                                    renderInput={(params) => (
                                        <TextField {...params} label={
                                            k === 'revenue_account_id' ? 'Revenue account' :
                                            k === 'cogs_account_id' ? 'COGS account' : 'Inventory account'
                                        } />
                                    )}
                                />
                            ))}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Variants */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem' }}>
                                Variants
                            </Typography>
                            <Button size="small" startIcon={<AddIcon />} onClick={addVariant}
                                sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}>
                                Add variant
                            </Button>
                        </Stack>
                        {form.variants.length === 0 ? (
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', fontSize: '0.85rem' }}>
                                No variants — the parent SKU is tracked directly.
                            </Typography>
                        ) : (
                            <Stack spacing={1}>
                                {form.variants.map((v, i) => (
                                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                                        <TextField size="small" label="SKU" sx={{ flex: 1 }}
                                            value={v.sku || ''} onChange={(e) => updateVariant(i, 'sku', e.target.value)} />
                                        <TextField size="small" label="Name" sx={{ flex: 2 }}
                                            value={v.name || ''} onChange={(e) => updateVariant(i, 'name', e.target.value)} />
                                        <TextField size="small" label="Barcode" sx={{ flex: 1 }}
                                            value={v.barcode || ''} onChange={(e) => updateVariant(i, 'barcode', e.target.value)} />
                                        <TextField size="small" label="Price" type="number" sx={{ flex: 1 }}
                                            value={v.unit_price ?? ''} onChange={(e) => updateVariant(i, 'unit_price', e.target.value)} />
                                        <TextField size="small" label="Cost" type="number" sx={{ flex: 1 }}
                                            value={v.unit_cost ?? ''} onChange={(e) => updateVariant(i, 'unit_cost', e.target.value)} />
                                        <IconButton onClick={() => removeVariant(i)} size="small"><DeleteIcon fontSize="small" /></IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
